import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Input, Row, Select, Space, Table, Tag, message } from 'antd';
import SuperAdminAPI from '../../../services/SuperAdminAPI';

const { RangePicker } = DatePicker;
const { Option } = Select;

const SEVERITY_COLORS = {
  LOW: 'green',
  MEDIUM: 'gold',
  HIGH: 'volcano',
  CRITICAL: 'red',
};

const formatDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const toIsoStringSafe = value => {
  if (!value) return null;
  if (typeof value.toISOString === 'function') {
    return value.toISOString();
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const summarizeDiff = (beforeSnapshot, afterSnapshot) => {
  if (!beforeSnapshot && !afterSnapshot) return '—';
  if (beforeSnapshot && !afterSnapshot) return 'Deleted';
  if (!beforeSnapshot && afterSnapshot) return 'Created';

  try {
    const keys = new Set([
      ...Object.keys(beforeSnapshot || {}),
      ...Object.keys(afterSnapshot || {}),
    ]);
    const changed = Array.from(keys).filter(key => {
      const beforeVal = JSON.stringify(beforeSnapshot?.[key] ?? null);
      const afterVal = JSON.stringify(afterSnapshot?.[key] ?? null);
      return beforeVal !== afterVal;
    });
    if (!changed.length) return 'No field-level changes';
    return changed.slice(0, 4).join(', ');
  } catch (error) {
    return 'Change summary unavailable';
  }
};

const PlatformAuditLogPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [severity, setSeverity] = useState(undefined);
  const [dateRange, setDateRange] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
  });

  const loadLogs = useCallback(async (page = 1, limit = pagination.limit) => {
    setLoading(true);
    try {
      const [dateFromValue, dateToValue] = Array.isArray(dateRange) ? dateRange : [];
      const result = await SuperAdminAPI.getPlatformAuditLogs({
        page,
        limit,
        actor: actor || undefined,
        action: action || undefined,
        severity: severity || undefined,
        dateFrom: toIsoStringSafe(dateFromValue) || undefined,
        dateTo: toIsoStringSafe(dateToValue) || undefined,
      });

      const items = result?.items || [];
      const pageInfo = result?.pagination || {};
      setLogs(items);
      setPagination({
        page: Number(pageInfo.page) || page,
        limit: Number(pageInfo.limit) || limit,
        total: Number(pageInfo.total) || 0,
      });
    } catch (error) {
      console.error('PlatformAuditLogPanel: failed to load logs', error);
      message.error('Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, actor, action, severity, dateRange]);

  useEffect(() => {
    loadLogs(1, pagination.limit);
  }, []);

  const handleApplyFilters = () => {
    loadLogs(1, pagination.limit);
  };

  const handleResetFilters = () => {
    setActor('');
    setAction('');
    setSeverity(undefined);
    setDateRange([]);
    setTimeout(() => loadLogs(1, pagination.limit), 0);
  };

  const tableData = useMemo(() => logs.map(item => ({
    ...item,
    key: item._id || `${item.action}-${item.timestamp}`,
  })), [logs]);

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      width: 190,
      render: value => formatDateTime(value),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      width: 120,
      render: value => (
        <Tag color={SEVERITY_COLORS[String(value || '').toUpperCase()] || 'default'}>
          {String(value || 'LOW').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 220,
      render: value => value || '—',
    },
    {
      title: 'Actor',
      dataIndex: 'actor',
      width: 260,
      render: value => {
        const actorInfo = value || {};
        return (
          <div>
            <div>{actorInfo.email || actorInfo.name || actorInfo.id || 'system'}</div>
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>{actorInfo.role || '—'}</div>
          </div>
        );
      },
    },
    {
      title: 'Target',
      dataIndex: 'target',
      width: 240,
      render: value => {
        const target = value || {};
        return (
          <div>
            <div>{target.name || target.id || '—'}</div>
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>{target.type || '—'}</div>
          </div>
        );
      },
    },
    {
      title: 'IP Address',
      dataIndex: ['request', 'ipAddress'],
      width: 170,
      render: value => value || '—',
    },
    {
      title: 'Before/After',
      key: 'diff',
      render: (_, record) => summarizeDiff(record.beforeSnapshot, record.afterSnapshot),
    },
  ];

  return (
    <Card title="Platform Audit Logs">
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Actor email or name"
            value={actor}
            onChange={event => setActor(event.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Action (e.g. tenant_status_changed)"
            value={action}
            onChange={event => setAction(event.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Select
            placeholder="Severity"
            value={severity}
            onChange={setSeverity}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="LOW">LOW</Option>
            <Option value="MEDIUM">MEDIUM</Option>
            <Option value="HIGH">HIGH</Option>
            <Option value="CRITICAL">CRITICAL</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={12} lg={8}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: '100%' }}
            showTime
          />
        </Col>
      </Row>

      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={handleApplyFilters} loading={loading}>
          Apply Filters
        </Button>
        <Button onClick={handleResetFilters} disabled={loading}>
          Reset
        </Button>
      </Space>

      <Table
        rowKey="key"
        loading={loading}
        columns={columns}
        dataSource={tableData}
        scroll={{ x: 1300 }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: true,
        }}
        onChange={nextPagination => {
          loadLogs(nextPagination.current || 1, nextPagination.pageSize || pagination.limit);
        }}
      />
    </Card>
  );
};

export default PlatformAuditLogPanel;
